import React from "react";
import Head from "expo-router/head";
import { getAppUrl } from "../../lib/url";
import { getSharedLinkMeta } from "./sharedLinkMeta";

interface AnalysisData {
  title?: string;
  description?: string;
  score?: number;
  createdAt?: string;
}

interface SharedLinkHeaderProps {
  shortId: string;
  analysisData: AnalysisData | null;
}

/**
 * Renders Open Graph and Twitter Card meta tags for shared links.
 *
 * Dynamic OG images: when a valid shortId is present and the CF Worker URL is
 * configured, the og:image points to the generate-og-image endpoint which
 * renders a per-analysis 1200x630 PNG with score, category, and summary.
 *
 * Static fallback: /og-image.png (branded DramaDetector image in apps/mobile/public/).
 * Used when the edge function URL cannot be constructed.
 */
export function SharedLinkHeader({ shortId, analysisData }: SharedLinkHeaderProps) {
  const appUrl = getAppUrl();

  // Build canonical URL for this short link
  const canonicalUrl = `${appUrl}/s/${shortId}`;

  // Dynamic OG image: edge function generates per-analysis 1200x630 PNG
  const cfWorkerUrl = process.env.EXPO_PUBLIC_CF_WORKER_URL || "";
  const baseUrl = cfWorkerUrl.replace(/\/+$/, ''); // strip trailing slash
  const staticFallback = `${appUrl}/og-image.png`;
  const ogImage = baseUrl && shortId
    ? `${baseUrl}/generate-og-image?id=${encodeURIComponent(shortId)}`
    : staticFallback;
  // NOTE: OG meta tags are server-rendered for crawlers (Twitter/Facebook/LinkedIn).
  // useTranslation() is a client-side hook that won't execute during SSR/SSG meta
  // rendering. These strings are intentionally hardcoded English for now.
  // TODO: When adding i18n for OG tags, use server-side translation (i18next.t())
  // or pass localized strings from the page route's data loader.
  const { title: ogTitle, description: ogDescription } = getSharedLinkMeta(
    analysisData,
  );

  return (
    <Head>
      <title>{ogTitle}</title>
      <meta name="description" content={ogDescription} />
      <meta property="og:type" content="article" />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="DramaDetector Analysis Preview" />
      <meta property="og:site_name" content="DramaDetector" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={ogDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content="DramaDetector Analysis Preview" />
      <meta name="robots" content="noindex, nofollow" />
      <link rel="canonical" href={canonicalUrl} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            name: ogTitle,
            headline: ogTitle,
            description: ogDescription,
            url: canonicalUrl,
            image: ogImage,
            datePublished: analysisData?.createdAt || new Date().toISOString(),
            publisher: {
              "@type": "Organization",
              name: "DramaDetector",
              url: appUrl,
            },
          }),
        }}
      />
    </Head>
  );
}
